import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import clsx from "clsx";
import {useFormikContext} from "formik";
import {isEqual} from "lodash";
import Swal from "sweetalert2";

import {accessAllowed} from "../../../../gitIgnore/accessAllowedIds";
import useContextMenu from "../../../../hook/useContextMenu";
import useCountryOptions from "../../../../hook/useCountryOptions";
import {selectAllCountries} from "../../../../slices/CatalogsSlice";
import {getClients} from "../../../../slices/PersonSlice";
import {getCurrentUser} from "../../../../slices/UserSlice";
import FunctionButton from "../../../../utilities/Buttons/FunctionButton";
import {calcCoStatusColor} from "../../../../utilities/calcStatusColor";
import {STATUS_COLORS} from "../../../../utilities/colorConstants";
import ContextMenuComposed from "../../../../utilities/ContextMenuComposed/ContextMenuComposed";
import {formatNumberWithCurrency} from "../../../../utilities/currencyFormat";
import {dateFormat} from "../../../../utilities/dateFormat";
import Divider from "../../../../utilities/Divider/Divider";
import {BALANCE_SHEET_ACCOUNT_TYPES, EUR_CURRENCY_ID} from "../../../../utilities/generalConstants.js/generalConstants";
import {swalCustom} from "../../../../utilities/hooks/swalCustom";
import {toaster} from "../../../../utilities/hooks/toaster";
import makeNameFromPersona from "../../../../utilities/makeNameFromPersona";
import {useFiltersContext} from "../../../conteiners/filters/useSessionFiltersContext";
import ReactTable from "../../../conteiners/ReactTable";
import {MoneyInput, TextInput} from "../../../Inputs";
import CountrySelect from "../../../Inputs/CountrySelect/CountrySelect";
import IndexSelect from "../../../Inputs/IndexSelect";
import {Select} from "../../../Inputs/SelectField";
import ModalForm from "../../../Modal/FormModal";
import useColumns from "../../../Requests/OrderProcessing/utils/hooks/useColumns";
import {Status} from "../../ProjectAddForm/ProjectAddForm";
import RecipientDetails from "../../ProjectModals/RecipientDetails";
import {
  addProject,
  deleteProject,
  deselectProject,
  fetchProjectById,
  fetchProjectFilters,
  fetchProjectsWithFilter,
  getCurrentProject,
  getProjectLoading,
  getProjects,
  updateProject,
} from "../../ProjectSlice";
import {PROJECT_TABLE_COLUMNS} from "../constants";

import "../projectSecond.scss";

const ProjectSection = ({readOnly, handleDocumentOpen}) => {
  const {
    i18n: {language},
    t,
  } = useTranslation();
  const {menuVisible, menuPosition, handleContextMenu} = useContextMenu();
  const {setFieldValue, values, setValues} = useFormikContext();
  const {filters} = useFiltersContext();
  const countries = useSelector(selectAllCountries);
  const dispatch = useDispatch();

  const user = useSelector(getCurrentUser);

  const projectTableColumns = useColumns(PROJECT_TABLE_COLUMNS);
  const {countriesOptions} = useCountryOptions(countries);

  const currentProject = useSelector(getCurrentProject);
  const projects = useSelector(getProjects);
  const clients = useSelector(getClients);
  const loading = useSelector(getProjectLoading);

  const [recipientDetailsModalActive, setRecipientDetailsModalActive] = useState(false);

  const clientsCatalog = useMemo(
    () =>
      clients?.map(client => {
        return {
          title: client.clientNumber,
          value: client.clientId,
        };
      }) || [],
    [clients],
  );

  // function calcProjectStatusColor(project) {
  //   if (!project || !project.cos) return STATUS_COLORS.GREY;
  //   let hasYellowTasks = false;
  //   let hasGreenTasks = false;
  //   // let hasTransparentTasks = false;
  //   // let hasGreyTasks = false;

  //   let allGreenTasks = true;
  //   let allTransparentTasks = true;

  //   for (let i = 0; i < project.cos.length; ++i) {
  //     const {statusId, startDate, bringTo, isDone} = project.cos[i];
  //     const statusColor = calcCoStatusColor({startDate, bringTo, statusId, isDone});
  //     // statusId >= 3 ? STATUS_COLORS.GREEN : calcStatusColor({startDate, finalDate});

  //     switch (statusColor) {
  //       case STATUS_COLORS.RED:
  //         return STATUS_COLORS.RED;
  //       case STATUS_COLORS.YELLOW:
  //         hasYellowTasks = true;
  //         allGreenTasks = false;
  //         allTransparentTasks = false;
  //         break;
  //       case STATUS_COLORS.GREEN:
  //         hasGreenTasks = true;
  //         allTransparentTasks = false;
  //         break;
  //       case STATUS_COLORS.TRANSPARENT:
  //         // hasTransparentTasks = true;
  //         allGreenTasks = false;
  //         break;
  //       case STATUS_COLORS.GREY:
  //         // hasGreyTasks = true;
  //         allTransparentTasks = false;
  //         allGreenTasks = false;
  //         break;
  //       default:
  //         allGreenTasks = false;
  //         allTransparentTasks = false;
  //     }
  //   }

  //   return project.cos.length === 0 || allTransparentTasks
  //     ? STATUS_COLORS.TRANSPARENT
  //     : hasYellowTasks
  //     ? STATUS_COLORS.YELLOW
  //     : allGreenTasks || (hasGreenTasks && !hasYellowTasks)
  //     ? STATUS_COLORS.GREEN
  //     : STATUS_COLORS.GREY;
  // }

  function transformProjectForTable(project, i) {
    return {
      statusCircle: calcProjectStatusColor(project),
      number: i + 1,
      dateFrom: dateFormat(project.createdAt),
      dateTo: dateFormat(project?.contracts?.find(contract => contract.deadline)?.deadline),
      projectNumber: project?.projectNumber,
      address: project?.delivery?.address || "",
      planSum: formatNumberWithCurrency(project?.butget),
      factSum: formatNumberWithCurrency(project?.factButget),
      projectDesign: project.designNumber,
      projectManager: makeNameFromPersona({persona: project?.projectManager?.persona, language}) || "?",

      id: project.projectId,
    };
  }

  const tableData = useMemo(() => projects?.map(transformProjectForTable) || [], [projects]);

  // useEffect(() => {
  //   return () => {
  //     dispatch(deselectProject());
  //   };
  // }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;
    fetchProjects({...(filters || {}), signal});

    return () => {
      abortController.abort();
    };
  }, [JSON.stringify(filters || {})]);

  useEffect(() => {
    if (!currentProject?.projectId) return;

    dispatch(fetchProjectById(currentProject?.projectId));
  }, [currentProject?.projectId]);

  const fetchProjects = params => {
    dispatch(fetchProjectsWithFilter(params));
  };

  const clientBalance = currentProject?.client?.accounts?.find(
    item =>
      item.currencyId === EUR_CURRENCY_ID && item.balanceSheetAccId === BALANCE_SHEET_ACCOUNT_TYPES.CLIENT_ACCOUNT,
  )?.balance;

  useEffect(() => {
    setTimeout(() => {
      setValues(prev => ({
        ...prev,
        projectBudget: currentProject?.butget ?? "",
        project: currentProject?.projectNumber || "",
        projectOffice: currentProject?.orgStructure[language === "eng" ? "fullNameEng" : "fullNameUkr"] || "",
        projectManager: currentProject?.projectManager.name || "",
        statusId: currentProject?.statusId || "",
        countryId: currentProject?.delivery?.countryId || "",
        deliveryPoint: currentProject?.delivery?.city || "",
        deliveryPointIndex: currentProject?.delivery?.index || "",
        deliveryPointAddress: currentProject?.delivery?.address || "",
        projectDesign: currentProject?.designNumber || "",
        clientNumber: currentProject?.client?.clientId || "",
        clientBalance,
        factButget: currentProject?.factButget,
      }));
    }, 10);
  }, [
    currentProject?.projectId,
    currentProject?.butget,
    currentProject?.factButget,
    currentProject?.projectNumber,
    currentProject?.orgStructure[language === "eng" ? "fullNameEng" : "fullNameUkr"],
    currentProject?.statusId,
    currentProject?.projectManager.name,
    currentProject?.delivery?.countryId,
    currentProject?.delivery?.city,
    currentProject?.delivery?.index,
    currentProject?.delivery?.address,
    currentProject?.designNumber,
    currentProject?.client?.clientId,
    clientBalance,
  ]);

  const onCreateProject = () => {
    dispatch(addProject({clientId: values?.clientNumber, countryId: values?.countryId}))
      .then(() => {
        toaster.success("Project created");
        fetchProjects(filters);
        dispatch(fetchProjectFilters());
      })
      .catch(err => console.error(err));
  };

  const onUpdateProject = () => {
    dispatch(
      updateProject({
        projectId: currentProject.projectId,
        data: {
          designNumber: values?.projectDesign,
          statusId: values?.statusId,
          delivery: {
            countryId: values?.countryId || null,
            city: values?.deliveryPoint,
            index: values?.deliveryPointIndex,
            address: values?.deliveryPointAddress,
          },
        },
      }),
    ).then(() => {
      dispatch(fetchProjectFilters());
      toaster.success(t("Project updated ", {projectNumber: currentProject?.projectNumber}));
    });
  };

  const onDeleteProject = () => {
    if (accessAllowed.includes(user.id)) {
      Swal.fire({
        title: "",
        text: t("Are you sure?"),
        icon: "warning",
        confirmButtonText: t("Yes"),
        showCancelButton: true,
        cancelButtonText: t("Cancel"),
      }).then(value => {
        if (value.isConfirmed) {
          dispatch(deleteProject(currentProject.projectId)).then(() => fetchProjects(filters));
        }
      });
    } else {
      Swal.fire({
        title: "",
        text: `${t("Insufficient")}`,
        icon: "info",
        confirmButtonText: t("Ok"),
      });
    }
  };

  const onProjectSelect = useCallback(value => {
    if (!value) {
      dispatch(deselectProject());
      return;
    }
    dispatch(fetchProjectById(value));
  }, []);

  const fieldsDisabled = false;

  const editProjectDisabled =
    !currentProject ||
    isEqual(
      {
        designNumber: values?.projectDesign || "",
        countryId: values?.countryId || "",
        city: values?.deliveryPoint || "",
        index: values?.deliveryPointIndex || "",
        address: values?.deliveryPointAddress || "",
        statusId: values?.statusId || "",
      },
      {
        designNumber: currentProject?.designNumber || "",
        countryId: currentProject?.delivery?.countryId || "",
        city: currentProject?.delivery?.city || "",
        index: currentProject?.delivery?.index || "",
        address: currentProject?.delivery?.address || "",
        statusId: currentProject?.statusId || "",
      },
    );

  function onModalClose({areValuesChanged}) {
    if (areValuesChanged) {
      swalCustom.unsavedData({
        confirmFunc: () => {
          setRecipientDetailsModalActive(false);
        },
      });
      return;
    }

    setRecipientDetailsModalActive(false);
  }

  return (
    <div className="left-side field-style" id="context-menu-container">
      <div className="fdr gap mb0_5 jcsb">
        <Select
          label={"Client №"}
          name="clientNumber"
          options={clientsCatalog}
          autolabel
          required
          makeEffect={option => setFieldValue("clientBalance", option?.clientBalance)}
          readOnly={currentProject || readOnly}
        />
        <CountrySelect
          label="Delivery country"
          name="countryId"
          baseOptions={countriesOptions}
          width="10em"
          autolabel
          generalClassName={clsx("left-drop", currentProject && "hiddenEl")}
          makeEffect={() => {
            setFieldValue("deliveryPointIndex", "");
            setFieldValue("deliveryPoint", "");
            setFieldValue("deliveryPointAddress", "");
          }}
          required
          readOnly={readOnly}
        />
        <Select
          label="Partner"
          name="partnerId"
          options={[]}
          autolabel
          readOnly={currentProject || readOnly}
          noFunctional
        />
      </div>
      <TextInput
        label="Project Design"
        name="projectDesign"
        style={{width: "100%"}}
        readOnly={!currentProject || readOnly}
      />

      <div className="finBlock">
        <div className="project_block">
          <FunctionButton
            name="Create p"
            onClick={onCreateProject}
            disabled={!!currentProject || !values?.clientNumber || values?.clientNumber === "" || !values?.countryId}
            readOnly={readOnly}
          />
          <TextInput
            label="Project"
            name="project"
            autolabel
            style={{width: "15rem"}}
            readOnly
            inputClassName="txtField"
          />
        </div>
      </div>

      <div className="mb0_5" style={{border: "1px solid #DAE1EC", padding: "0.5rem"}}>
        <div className="fdr gap mb0_5">
          <CountrySelect
            label="Delivery country"
            name="countryId"
            baseOptions={countriesOptions}
            width="10rem"
            autolabel
            generalClassName={"left-drop"}
            makeEffect={() => {
              setFieldValue("deliveryPointIndex", "");
              setFieldValue("deliveryPoint", "");
              setFieldValue("deliveryPointAddress", "");
            }}
            readOnly={!currentProject || readOnly}
          />
          <IndexSelect
            readOnly={!currentProject || readOnly}
            countryId={currentProject ? values?.countryId : null}
            currentIndex={currentProject?.delivery?.index}
            currentProjectId={currentProject?.projectId}
          />
        </div>
        <TextInput
          label="Delivery point address"
          name="deliveryPointAddress"
          autolabel
          readOnly={!currentProject || readOnly}
        />
      </div>

      <div className="fdr mb0_5 jcsb gap">
        <FunctionButton
          name="Recipient*"
          disabled={!currentProject}
          onClick={() => {
            setRecipientDetailsModalActive(true);
          }}
          readOnly={readOnly}
        />

        <FunctionButton
          name="Save Project"
          onClick={onUpdateProject}
          disabled={editProjectDisabled}
          readOnly={readOnly}
        />
        <FunctionButton
          name="Delete p"
          onClick={onDeleteProject}
          disabled={!currentProject || currentProject.cos?.length > 0}
          readOnly={readOnly}
          tooltipMessage={(currentProject && currentProject.cos?.length > 0 && "Info delete project") || ""}
        />
      </div>
      <Divider margin="0.5rem" />
      <div style={{border: "1px solid #DAE1EC", padding: "0.5rem", marginBottom: "1rem"}}>
        <div className="infoBlock">
          <TextInput label="Project office" name="projectOffice" style={{width: "20rem"}} readOnly />
          <TextInput label="Manager" name="projectManager" style={{width: "20rem"}} readOnly />
          <Status readOnly={fieldsDisabled || !currentProject} style={{width: "20rem"}} />
        </div>
        <div className="balanceBlock">
          <MoneyInput label="Balance" name="clientBalance" width="8rem" readOnly />
          <MoneyInput label="Project Balance" name="factButget" autolabel width="8rem" readOnly />
          <MoneyInput label="Project budget" name="projectBudget" autolabel width="8rem" readOnly />
        </div>
      </div>
      <ReactTable
        defaultData={tableData}
        columns={projectTableColumns}
        current={currentProject?.projectId || null}
        onSelect={onProjectSelect}
        className="scrollX  mb0_5 fGrow"
        loading={loading}
        selectOnRightBtn
        handleContextMenu={handleContextMenu}
      />
      <ModalForm
        closable={true}
        canBeClosed={true}
        Component={RecipientDetails}
        label={t("Details of the recipient")}
        active={recipientDetailsModalActive}
        setModalActive={setRecipientDetailsModalActive}
        onModalClose={onModalClose}
        className="smallModal"
      />
      <div id="context-menu-container">
        <ContextMenuComposed menuPosition={menuPosition} menuVisible={menuVisible}>
          <FunctionButton
            name="Open project documents"
            disabled={!currentProject}
            onClick={handleDocumentOpen}
            hidden
          />
        </ContextMenuComposed>
      </div>
    </div>
  );
};

export default ProjectSection;

export function calcProjectStatusColor(project) {
  if (!project || !project.cos) return STATUS_COLORS.GREY;
  let hasYellowTasks = false;
  let hasGreenTasks = false;
  // let hasTransparentTasks = false;
  // let hasGreyTasks = false;

  let allGreenTasks = true;
  let allTransparentTasks = true;

  for (let i = 0; i < project.cos.length; ++i) {
    const {statusId, startDate, bringTo, isDone} = project.cos[i];
    const statusColor = calcCoStatusColor({startDate, bringTo, statusId, isDone});
    // statusId >= 3 ? STATUS_COLORS.GREEN : calcStatusColor({startDate, finalDate});

    switch (statusColor) {
      case STATUS_COLORS.RED:
        return STATUS_COLORS.RED;
      case STATUS_COLORS.YELLOW:
        hasYellowTasks = true;
        allGreenTasks = false;
        allTransparentTasks = false;
        break;
      case STATUS_COLORS.GREEN:
        hasGreenTasks = true;
        allTransparentTasks = false;
        break;
      case STATUS_COLORS.TRANSPARENT:
        // hasTransparentTasks = true;
        allGreenTasks = false;
        break;
      case STATUS_COLORS.GREY:
        // hasGreyTasks = true;
        allTransparentTasks = false;
        allGreenTasks = false;
        break;
      default:
        allGreenTasks = false;
        allTransparentTasks = false;
    }
  }

  return project.cos.length === 0 || allTransparentTasks
    ? STATUS_COLORS.TRANSPARENT
    : hasYellowTasks
    ? STATUS_COLORS.YELLOW
    : allGreenTasks || (hasGreenTasks && !hasYellowTasks)
    ? STATUS_COLORS.GREEN
    : STATUS_COLORS.GREY;
}
